import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Check, ChevronDown } from 'lucide-react';
import SimulateurRevenus from '../components/SimulateurRevenus';
import Logo from '../components/Logo';
import axios from 'axios';
import { getRestApiBaseUrl } from '../amplifyConfigure';

const API_URL = getRestApiBaseUrl();

const FAQ_DATA = [
  {
    question: "L'inscription est-elle gratuite ?",
    answer:
      "Oui, l'inscription sur AssisToday est entièrement gratuite pour les assistantes dentaires. Aucun frais d'adhésion, aucune commission prélevée sur vos revenus. Nous nous rémunérons auprès des cabinets dentaires partenaires.",
  },
  {
    question: "AssisToday est-elle une société d'intérim ?",
    answer:
      "Non. AssisToday n'est pas une société d'intérim. Le cabinet ou le centre dentaire reste votre employeur et vous verse directement le salaire. AssisToday facilite la mise en relation et encourage le recours au travail déclaré à chaque mission grâce à l'outil de signature électronique intégré à la plateforme pour formaliser l'accord.",
  },
  {
    question: "Puis-je choisir mes missions ?",
    answer:
      "Oui. Vous restez libre d'accepter uniquement les missions qui correspondent à vos disponibilités et à vos critères. Aucune limite de candidatures, aucune pénalité si vous ne postulez pas.",
  },
  {
    question: "Puis-je rejoindre le réseau si je travaille déjà dans un cabinet ?",
    answer:
      "Absolument. AssisToday est conçu pour compléter votre activité principale, pas pour la remplacer. Vous pouvez effectuer des vacations ponctuelles dans d'autres cabinets sur vos jours libres, à condition de ne pas être soumise à une clause d'exclusivité et de respecter les durées maximales légales de travail (10h par jour, 46h par semaine et 44h en moyenne sur 12 semaines).",
  },
];

const CARDS_DATA = [
  {
    num: '01',
    title: 'Inscrivez-vous',
    body: 'Complétez le formulaire ci-dessus pour être recontactée en priorité avant le lancement.',
  },
  {
    num: '02',
    title: 'Recevez des missions',
    body: 'Acceptez ou refusez librement, sans justification.',
  },
  {
    num: '03',
    title: 'Signez sereinement',
    body: 'Formalisez vos accords par signature électronique.',
  },
];

const JOURS_OPTIONS = [
  { value: '', label: 'Choisir' },
  { value: '1 jour / semaine', label: '1 jour / semaine' },
  { value: '2 jours / semaine', label: '2 jours / semaine' },
  { value: '3 jours / semaine', label: '3 jours / semaine' },
  { value: '4 jours / semaine', label: '4 jours / semaine' },
  { value: '5 jours / semaine', label: '5 jours / semaine' },
];

const inputClass =
  'w-full min-w-0 border border-gray-200 rounded-2xl px-3.5 py-3 text-[#1A3B48] placeholder:text-[#9CA3AF] bg-white text-sm font-normal transition-colors duration-200 ease-out hover:border-[#05BF87] focus:outline-none focus:border-[#05BF87]';
const labelClass = 'block text-sm font-medium text-[#1A3B48] mb-1';

/** Entrée en page : fondu + léger translate-y, décalage séquentiel (ms). */
const reveal = (delayMs) => ({
  className: 'opacity-0 animate-fade-in-up',
  style: { animationDelay: `${delayMs}ms` },
});

export default function LandingPage() {
  const [form, setForm] = useState({ prenom: '', nom: '', telephone: '', departement: '', jours_semaine: '' });
  const [showError, setShowError] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const errorRef = useRef(null);

  const scrollTo = (id) => {
    if (id === 'top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  const isValid = () => Object.values(form).every((v) => v.trim());

  const handleChange = (field, value) => {
    const next = { ...form, [field]: value };
    setForm(next);
    if (showError && Object.values(next).every((v) => v.trim())) setShowError(false);
  };

  const handleSubmit = async () => {
    if (!isValid()) {
      setShowError(true);
      setTimeout(() => errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 50);
      return;
    }
    setShowError(false);
    setSubmitError(null);
    if (!API_URL) {
      setSubmitError("L'inscription est momentanément indisponible.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/inscriptions`, form, { timeout: 15000 });
      setSubmitted(true);
    } catch (e) {
      console.error('Erreur inscription:', e);
      setSubmitError("L'inscription n'a pas pu être enregistrée. Réessayez plus tard.");
    } finally {
      setLoading(false);
    }
  };

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div style={{ fontFamily: "'Circular Std', sans-serif" }} className="text-[#1A3B48]">

      {/* ── NAVBAR ── */}
      <nav
        className={`sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-100 h-[64px] flex items-center justify-between px-6 lg:px-12 ${reveal(0).className}`}
        style={reveal(0).style}
        data-testid="navbar"
      >
        <div
          className="cursor-pointer transition-transform duration-300 ease-out hover:scale-[0.96] active:scale-[0.94]"
          onClick={() => scrollTo('top')}
          role="button"
          aria-label="Retour en haut de page"
          data-testid="navbar-logo"
        >
          <Logo textColor="#1A3B48" iconColor="#05BF87" height={64} />
        </div>
        <button
          type="button"
          onClick={() => scrollTo('formulaire')}
          className="at-btn-shine group bg-[#05BF87] text-white font-medium px-5 py-2.5 rounded-xl transition-all duration-300 ease-out hover:brightness-105 hover:shadow-md hover:shadow-[#05BF87]/35 text-sm"
          aria-label="Aller au formulaire d'inscription"
          data-testid="navbar-cta"
        >
          <span className="relative z-10">Je m'inscris →</span>
        </button>
      </nav>

      {/* ── HERO (bannière + formulaire) ── */}
      <section
        id="formulaire"
        className={`bg-gradient-to-b from-[#F0F7FC] via-[#F5FAFD] to-[#E6EEF6] ${reveal(120).className}`}
        style={reveal(120).style}
      >
        <div
          className="bg-[#1A3B48] px-4 py-2.5 text-xs sm:text-sm font-medium tracking-wide text-white"
          data-testid="early-access-banner"
        >
          <div className="mx-auto flex max-w-4xl items-center justify-center gap-2.5 text-center">
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full bg-[#05BF87] shadow-[0_0_10px_rgba(5,191,135,0.55)] animate-soft-blink"
              aria-hidden
            />
            <span>Accès anticipé aux inscriptions ouvert</span>
          </div>
        </div>
        <div className="pt-4 pb-14 sm:pt-10 sm:pb-18 lg:py-24">
          <div className="max-w-2xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-5 sm:mb-10">
              <span className="inline-block bg-[#05BF87]/10 text-[#05BF87] text-xs font-semibold uppercase tracking-widest px-3 py-1 rounded-full mb-3 sm:mb-4">
                INSCRIPTION GRATUITE
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A3B48] mb-3 tracking-tight">
                Inscrivez-vous{' '}
                <span className="bg-gradient-to-r from-[#028a6b] via-[#05BF87] to-[#5fe3b3] bg-clip-text text-transparent">
                  en avant-première
                </span>
              </h2>
              <p className="text-gray-500 text-sm sm:text-base max-w-lg mx-auto leading-relaxed">
                Rejoignez le réseau AssisToday et accédez en priorité aux missions près de chez vous, dès le lancement.
              </p>
            </div>

            <div
              className={`bg-white rounded-2xl p-5 sm:p-8 ${
                submitted
                  ? 'border border-[#00BA88] shadow-sm'
                  : 'border-2 border-[#00BA88] shadow-xl'
              }`}
              data-testid="inscription-form"
            >
            {submitted ? (
              <div
                className="mx-auto max-w-md px-2 py-10 text-center sm:px-4 sm:py-12"
                data-testid="success-message"
              >
                <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-[#E8F8F2] sm:mb-7">
                  <svg
                    className="h-7 w-7 text-[#028a6b]"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold tracking-tight text-[#1A3B48] sm:text-[1.35rem]">
                  Vous êtes inscrite !
                </h3>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-[#475467] sm:mt-4 sm:text-[15px]">
                  Bienvenue dans le réseau fondateur AssisToday. Nous vous contacterons en priorité dès l’ouverture.
                </p>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#475467] mb-4 sm:mb-6 leading-relaxed">
                  • Assistantes dentaires{' '}
                  <span className="underline decoration-[#475467] underline-offset-2">diplômées</span> uniquement
                </p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-5 sm:gap-x-5">
                  <div className="min-w-0">
                    <label htmlFor="prenom" className={labelClass}>Prénom</label>
                    <input
                      id="prenom"
                      type="text"
                      placeholder="Marie"
                      value={form.prenom}
                      onChange={(e) => handleChange('prenom', e.target.value)}
                      className={inputClass}
                      data-testid="input-prenom"
                    />
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="nom" className={labelClass}>Nom</label>
                    <input
                      id="nom"
                      type="text"
                      placeholder="Dupont"
                      value={form.nom}
                      onChange={(e) => handleChange('nom', e.target.value)}
                      className={inputClass}
                      data-testid="input-nom"
                    />
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="telephone" className={labelClass}>Téléphone</label>
                    <input
                      id="telephone"
                      type="tel"
                      placeholder="06 00 00 00 00"
                      value={form.telephone}
                      onChange={(e) => handleChange('telephone', e.target.value)}
                      className={inputClass}
                      data-testid="input-telephone"
                    />
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="departement" className={labelClass}>Département(s)</label>
                    <input
                      id="departement"
                      type="text"
                      placeholder="Ex : 75011,"
                      value={form.departement}
                      onChange={(e) => handleChange('departement', e.target.value)}
                      className={inputClass}
                      data-testid="input-departement"
                    />
                  </div>
                  <div className="min-w-0">
                    <label htmlFor="jours_semaine" className={labelClass}>Jours disponibles / semaine</label>
                    <div className="relative">
                      <select
                        id="jours_semaine"
                        value={form.jours_semaine}
                        onChange={(e) => handleChange('jours_semaine', e.target.value)}
                        className={`${inputClass} cursor-pointer appearance-none pr-10`}
                        data-testid="select-jours-semaine"
                      >
                        {JOURS_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value} disabled={opt.value === ''}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500"
                        aria-hidden
                      />
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="at-btn-shine group mt-6 w-full bg-[#05BF87] text-white font-semibold py-4 rounded-xl cursor-pointer transition-all duration-300 ease-out hover:brightness-105 hover:shadow-md hover:shadow-[#05BF87]/35 text-sm disabled:opacity-60 disabled:hover:shadow-none"
                  aria-label="Soumettre le formulaire d'inscription"
                  data-testid="submit-inscription"
                >
                  <span className="relative z-10">
                    {loading ? 'Inscription en cours...' : 'Rejoindre le réseau en avant-première →'}
                  </span>
                </button>

                <div
                  ref={errorRef}
                  role="alert"
                  aria-live="polite"
                  data-testid="form-error"
                  className={`mt-4 bg-red-50 border border-red-400 text-red-600 rounded-md px-4 py-3 text-sm flex items-center gap-2 transition-all duration-200 ${
                    showError ? 'opacity-100 max-h-20' : 'opacity-0 max-h-0 overflow-hidden py-0 mt-0 border-0'
                  }`}
                >
                  <span aria-hidden="true">⚠️</span>
                  <span>Veuillez remplir tous les champs</span>
                </div>

                {submitError && (
                  <div
                    role="alert"
                    aria-live="assertive"
                    data-testid="submit-api-error"
                    className="mt-4 bg-red-50 border border-red-400 text-red-600 rounded-md px-4 py-3 text-sm"
                  >
                    {submitError}
                  </div>
                )}
              </>
            )}
            </div>
          </div>
        </div>
      </section>

      {/* ── SIMULATEUR DE REVENUS ── */}
      <div className={reveal(240).className} style={reveal(240).style}>
        <SimulateurRevenus />
      </div>

      {/* ── NOTRE MISSION ── */}
      <section
        className={`bg-white py-16 sm:py-20 lg:py-24 ${reveal(360).className}`}
        style={reveal(360).style}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 lg:items-start">
            <div>
              <h2 className="text-3xl sm:text-[2rem] font-bold text-[#1A3B48] tracking-tight leading-tight">
                Notre mission
              </h2>
              <p className="mt-4 text-base sm:text-lg italic text-[#486BBD] leading-relaxed max-w-xl">
                Un cadre de confiance pour vos missions de renforts ponctuels.
              </p>
            </div>
            <ul className="flex flex-col gap-5">
              {[
                <>
                  <strong className="font-semibold text-[#1A3B48]">Encourager le travail déclaré</strong> par la
                  signature en ligne intégrée
                </>,
                <>
                  Offrir aux assistantes dentaires un{' '}
                  <strong className="font-semibold text-[#1A3B48]">complément de revenus flexible</strong> sans quitter
                  leur poste actuel
                </>,
                <>
                  Aider les dentistes à trouver un{' '}
                  <strong className="font-semibold text-[#1A3B48]">renfort qualifié</strong> en quelques clics
                </>,
              ].map((content, i) => (
                <li key={i} className="flex gap-3.5">
                  <span
                    className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#00BA88]"
                    aria-hidden
                  >
                    <Check className="h-3.5 w-3.5 text-white stroke-[3]" />
                  </span>
                  <span className="text-[15px] sm:text-base text-[#1A3B48] leading-relaxed pt-0.5">{content}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mx-auto mt-12 max-w-6xl px-6 sm:px-10 lg:px-16" aria-hidden>
          <div className="mx-auto h-0.5 w-full max-w-5xl sm:max-w-6xl rounded-full bg-[#05BF87]" />
        </div>
      </section>

      {/* ── COMMENT ÇA MARCHE ── */}
      <section
        className={`bg-gradient-to-b from-[#F0F7FC] via-[#F5FAFD] to-[#E6EEF6] py-16 sm:py-20 lg:py-24 ${reveal(480).className}`}
        style={reveal(480).style}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-center text-3xl sm:text-[2rem] font-bold text-[#1A3B48] tracking-tight mb-12 sm:mb-14">
            Comment ça marche
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 md:items-stretch">
            {CARDS_DATA.map((card) => (
              <div
                key={card.num}
                className="group relative flex h-full flex-col rounded-2xl border-2 border-[#00BA88] bg-white p-8 pt-10 transition-all duration-300 ease-out hover:-translate-y-2 hover:border-[#05BF87] hover:shadow-xl hover:shadow-[#00BA88]/25 hover:ring-2 hover:ring-[#05BF87]/25"
                data-testid={`card-${card.num}`}
              >
                <span
                  className="absolute left-8 top-6 text-[4.5rem] sm:text-[5rem] font-bold leading-none tabular-nums select-none"
                  style={{ color: 'rgba(0, 186, 136, 0.14)' }}
                >
                  {card.num}
                </span>
                <div className="relative mt-20 sm:mt-24">
                  <h3 className="text-xl font-bold text-[#1A3B48] mb-3">{card.title}</h3>
                  <p className="text-sm sm:text-[15px] text-[#486BBD] leading-relaxed">{card.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section
        id="faq"
        className={`bg-white py-20 lg:py-24 ${reveal(600).className}`}
        style={reveal(600).style}
      >
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-center text-3xl sm:text-[2rem] font-bold text-[#1A3B48] tracking-tight mb-12 sm:mb-14">
            Questions fréquentes
          </h2>
          <div className="divide-y divide-gray-100 border-t border-gray-100">
            {FAQ_DATA.map((item, i) => (
              <div key={i} className="group/faq">
                <button
                  type="button"
                  className="w-full flex justify-between items-center gap-3 py-5 px-2 text-left cursor-pointer rounded-lg transition-all duration-250 ease-out hover:bg-[#05BF87]/[0.07] hover:shadow-[inset_0_0_0_1px_rgba(5,191,135,0.12)]"
                  onClick={() => toggleFaq(i)}
                  aria-expanded={openFaq === i}
                  data-testid={`faq-item-${i}`}
                >
                  <span className="font-semibold text-[#1A3B48] pr-4 text-sm sm:text-base">{item.question}</span>
                  <ChevronDown
                    className={`h-5 w-5 flex-shrink-0 text-[#05BF87] transition-all duration-300 ease-out ${
                      openFaq === i
                        ? 'rotate-180'
                        : 'rotate-0 group-hover/faq:-translate-y-px group-hover/faq:scale-110'
                    }`}
                    aria-hidden
                  />
                </button>
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={{
                    maxHeight: openFaq === i ? '500px' : '0px',
                    opacity: openFaq === i ? 1 : 0,
                  }}
                >
                  <p className="px-2 pb-5 text-gray-500 text-sm leading-relaxed">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINALE ── */}
      <section
        className={`bg-gradient-to-b from-[#F0F7FC] via-[#F5FAFD] to-[#E6EEF6] py-20 lg:py-24 ${reveal(720).className}`}
        style={reveal(720).style}
      >
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-[#05BF87] rounded-3xl px-8 py-16 text-center text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
              <span className="text-white [text-shadow:0_1px_0_rgba(255,255,255,0.22),0_2px_5px_rgba(0,50,42,0.28)]">
                Les premières inscrites{' '}
              </span>
              <span className="text-[#1A3B48]">passeront en priorité</span>
            </h2>
            <p className="text-white/80 mb-8 max-w-lg mx-auto text-sm sm:text-base leading-relaxed">
              Inscrivez-vous maintenant pour être recontactée avant le lancement et accéder aux premières missions
              disponibles.
            </p>
            <button
              type="button"
              onClick={() => scrollTo('formulaire')}
              className="at-btn-shine group bg-[#1A3B48] text-white font-semibold px-8 py-4 rounded-xl cursor-pointer transition-all duration-300 ease-out hover:brightness-105 hover:shadow-lg hover:shadow-[#1A3B48]/40 text-sm"
              aria-label="Aller au formulaire d'inscription pour l'accès anticipé"
              data-testid="cta-final-button"
            >
              <span className="relative z-10">Je rejoins l&apos;accès anticipé →</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer
        className={`bg-[#1A3B48] py-12 ${reveal(840).className}`}
        style={reveal(840).style}
        data-testid="footer"
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-6">
            <div
              className="cursor-pointer"
              onClick={() => scrollTo('top')}
              role="button"
              aria-label="Retour en haut de page"
              data-testid="footer-logo"
            >
              <Logo textColor="#FFFFFF" iconColor="#05BF87" height={56} />
            </div>
            <nav className="flex flex-wrap gap-4 sm:gap-6 text-xs sm:text-sm justify-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
              <Link
                to="/mentions-legales"
                className="hover:underline transition-all"
                data-testid="footer-mentions"
              >
                Mentions légales
              </Link>
              <Link
                to="/confidentialite"
                className="hover:underline transition-all"
                data-testid="footer-confidentialite"
              >
                Politique de confidentialité
              </Link>
              <Link to="/cgv" className="hover:underline transition-all" data-testid="footer-cgv">
                CGV/CGU
              </Link>
            </nav>
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 text-center text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>
            © 2026 AssisToday. Tous droits réservés.
          </div>
        </div>
      </footer>
    </div>
  );
}
